'use client';
import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { httpsCallable, getFunctions } from 'firebase/functions';
import toast from 'react-hot-toast';
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

/**
 * Owner-only modal for handing off a team to another existing staff member.
 *
 * Same shape as TransferOwnershipModal (club) — pick from existing staff,
 * confirm by typing the team name, atomic batch on the server. The new
 * head coach must already be in the team's `members` subcollection (added
 * via the regular invite flow). Players + parents are filtered out client-
 * side; the server enforces the same rule.
 *
 * UX:
 *   • Pick a staff member from a radio list (loaded fresh each open)
 *   • Two-step confirm: pick → type the team name to confirm
 *   • Calls transferTeamOwnership Cloud Function
 *   • On success: toast + onTransferred() callback (parent reloads / nav)
 *
 * Props:
 *   open, onClose, team: { id, name },
 *   currentOwnerUid, onTransferred()
 */
export default function TransferTeamOwnershipModal({
  open,
  onClose,
  team,
  currentOwnerUid,
  onTransferred,
}) {
  const [selectedUid, setSelectedUid] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [members, setMembers] = useState(null); // null = loading
  const [loadError, setLoadError] = useState(null);

  // Reset state every time the modal opens for a fresh hand-off.
  // We also re-fetch members each open in case the staff list changed
  // since the last time the modal was used (someone got added/removed
  // in another tab).
  useEffect(() => {
    if (!open || !team?.id) return;
    setSelectedUid(null);
    setConfirmText('');
    setSubmitting(false);
    setMembers(null);
    setLoadError(null);

    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'teams', team.id, 'members'));
        if (cancelled) return;
        const rows = snap.docs.map((d) => ({ uid: d.id, ...d.data() }));
        setMembers(rows);
      } catch (err) {
        if (cancelled) return;
        console.error('[TransferTeamOwnershipModal] member load failed', err);
        setLoadError(err?.message || 'Could not load team members.');
        setMembers([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, team?.id]);

  // Filter the staff candidates:
  //   • Skip the current owner (can't hand off to yourself)
  //   • Skip players + parents (only staff get promoted)
  //   • Skip rows missing a uid (defensive)
  // Sort by display name for predictable picker order.
  const candidates = useMemo(() => {
    if (!members) return [];
    const FORBIDDEN = new Set(['player', 'parent', 'guardian']);
    return members
      .filter((m) => m && m.uid && m.uid !== currentOwnerUid)
      .filter((m) => !FORBIDDEN.has(String(m.role || '').toLowerCase()))
      .sort((a, b) => (a.displayName || a.email || '').localeCompare(b.displayName || b.email || ''));
  }, [members, currentOwnerUid]);

  if (!open) return null;

  const selected = candidates.find((m) => m.uid === selectedUid) || null;
  const expectedConfirm = team?.name || '';
  const confirmMatches = expectedConfirm && confirmText.trim() === expectedConfirm.trim();
  const canSubmit = !!selected && confirmMatches && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const fn = httpsCallable(getFunctions(), 'transferTeamOwnership');
      await fn({ teamId: team.id, newOwnerUid: selected.uid });
      toast.success(`Head coach role passed to ${selected.displayName || selected.email || 'new owner'}.`);
      if (typeof onTransferred === 'function') onTransferred(selected.uid);
      onClose();
    } catch (err) {
      console.error('[TransferTeamOwnershipModal] transfer failed', err);
      const msg = err?.message || 'Failed to transfer ownership.';
      const code = err?.code || '';
      if (code === 'functions/permission-denied') {
        toast.error('Only the current head coach can transfer ownership.');
      } else if (code === 'functions/failed-precondition') {
        toast.error(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/55 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="px-5 pt-5 pb-4 border-b border-zinc-100 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
              <ExclamationTriangleIcon className="h-5 w-5" />
            </div>
            <div>
              <p
                className="text-[11px] font-bold uppercase tracking-[1.4px] text-amber-700"
                style={{ fontFamily: HEAD_FONT }}
              >
                Transfer head coach
              </p>
              <h2
                className="uppercase text-[#0d1f14] mt-0.5"
                style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 20 }}
              >
                {team?.name || 'this team'}
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 -m-1.5 rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <p className="text-sm text-zinc-700">
            The new head coach takes over full control of this team. You'll be demoted to <span className="font-semibold text-[#0d1f14]">Staff</span> — you keep editor access but they call the shots from here on.
          </p>

          {/* Staff picker */}
          {members === null ? (
            <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-500 flex items-center gap-2">
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
              Loading team staff…
            </div>
          ) : loadError ? (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4">
              <p className="text-sm font-semibold text-red-800">Couldn't load staff</p>
              <p className="text-xs text-red-700 mt-1">{loadError}</p>
            </div>
          ) : candidates.length === 0 ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm font-semibold text-amber-800">No staff to transfer to</p>
              <p className="text-xs text-amber-700 mt-1">
                You're the only staff member on this team. Invite an assistant coach or team manager first, then come back here to transfer.
              </p>
            </div>
          ) : (
            <div>
              <p
                className="text-[11px] font-bold uppercase tracking-[1.2px] text-zinc-500 mb-2"
                style={{ fontFamily: HEAD_FONT }}
              >
                Pick the new head coach
              </p>
              <ul className="space-y-2">
                {candidates.map((m) => {
                  const checked = selectedUid === m.uid;
                  return (
                    <li key={m.uid}>
                      <label
                        className={`flex items-center gap-3 rounded-xl border px-3 py-3 cursor-pointer transition-colors ${
                          checked
                            ? 'border-[#b5f03d]/40 bg-[#b5f03d]/5'
                            : 'border-zinc-200 bg-white hover:border-[#b5f03d]/40'
                        }`}
                      >
                        <input
                          type="radio"
                          name="newTeamOwner"
                          checked={checked}
                          onChange={() => setSelectedUid(m.uid)}
                          disabled={submitting}
                          className="h-4 w-4 border-zinc-300 text-[#5a8a1f] focus:ring-[#b5f03d]"
                        />
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
                          <UserCircleIcon className="h-5 w-5 text-[#5a8a1f]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-[#0d1f14] truncate">
                            {m.displayName || m.email || 'Staff member'}
                          </p>
                          <p className="text-xs text-zinc-500 truncate">
                            {m.email && m.displayName ? m.email : (m.role || 'Staff')}
                          </p>
                        </div>
                        {checked && (
                          <CheckCircleIcon className="h-5 w-5 text-[#5a8a1f] shrink-0" />
                        )}
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Confirm-by-typing — destructive-action UX. They must type the
              team name verbatim before the Transfer button enables. Same
              pattern as the club ownership transfer + the delete-club
              confirmation — coaches already know what to expect. */}
          {selected && (
            <div className="space-y-2">
              <p
                className="text-[11px] font-bold uppercase tracking-[1.2px] text-zinc-500"
                style={{ fontFamily: HEAD_FONT }}
              >
                To confirm, type <span className="text-[#0d1f14]">{expectedConfirm}</span>
              </p>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                disabled={submitting}
                placeholder={expectedConfirm}
                className="w-full rounded-xl border border-zinc-300 bg-white px-3 py-2 text-sm text-[#0d1f14] placeholder-zinc-400 focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/30 transition"
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-zinc-100 bg-zinc-50 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex-1 inline-flex items-center justify-center rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-sm font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all disabled:opacity-50"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-amber-600 hover:bg-amber-700 text-white px-4 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97] disabled:opacity-50 disabled:active:scale-100"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
          >
            {submitting ? (
              <>
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                Transferring…
              </>
            ) : (
              'Transfer head coach'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
